import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { ACCEPT_ARQUIVO_POR_FILTRO, ArquivoValor, TipoArquivoFiltro } from '@tera/shared/src/lib/miniFormularioValidacao';
import styles from './CampoArquivo.module.css';
import Icon from '@/components/Icon';
import { mdiCamera, mdiImage } from '@mdi/js';
import HBox from '@/components/layout/HBox';

type Props = {
    token: string;
    campoNome: string;
    filtro?: TipoArquivoFiltro;
    value?: ArquivoValor | null;
    onChange: (arquivo: ArquivoValor | null) => void;
    error?: string;
};

const formatarTamanho = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Heurística: iOS/Android quase sempre têm câmera traseira; no desktop (mesmo
// com webcam) o atributo `capture` é ignorado, então não faz sentido oferecer a escolha.
const isProvavelmenteMobile = () => {
    const userAgent = navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/.test(userAgent) || (userAgent.includes('Mac') && navigator.maxTouchPoints > 1);
    return isIOS || /Android/i.test(userAgent);
};

const CampoArquivo = ({ token, campoNome, filtro, value, onChange, error }: Props) => {
    const inputGaleriaRef = useRef<HTMLInputElement>(null);
    const inputCameraRef = useRef<HTMLInputElement>(null);
    const [enviando, setEnviando] = useState(false);
    const [erroEnvio, setErroEnvio] = useState<string | null>(null);
    const [modalAberto, setModalAberto] = useState(false);
    const [temCamera, setTemCamera] = useState(false);

    useEffect(() => {
        if (filtro !== 'imagem' || !isProvavelmenteMobile() || !navigator.mediaDevices?.enumerateDevices) return;
        let cancelado = false;
        navigator.mediaDevices.enumerateDevices()
            .then((devices) => {
                if (!cancelado && devices.some((d) => d.kind === 'videoinput')) {
                    setTemCamera(true);
                }
            })
            .catch(() => { });
        return () => {
            cancelado = true;
        };
    }, [filtro]);

    const enviarArquivo = async (arquivo: File) => {
        setEnviando(true);
        setErroEnvio(null);
        try {
            const formData = new FormData();
            formData.append('file', arquivo);
            const res = await axios.post(
                `/api/formulario-publico/${token}/upload`,
                formData,
                { params: { campo: campoNome } }
            );
            onChange(res.data?.arquivo ?? null);
        } catch (err: any) {
            setErroEnvio(err.response?.data?.erro ?? 'Falha ao enviar o arquivo. Tente novamente.');
        } finally {
            setEnviando(false);
        }
    };

    const handleSelecionar = (e: React.ChangeEvent<HTMLInputElement>) => {
        const arquivo = e.target.files?.[0];
        e.target.value = '';
        if (!arquivo) return;
        enviarArquivo(arquivo);
    };

    const handleClicarSelecionar = () => {
        if (temCamera) {
            setModalAberto(true);
        } else {
            inputGaleriaRef.current?.click();
        }
    };

    const accept = ACCEPT_ARQUIVO_POR_FILTRO[filtro ?? 'qualquer'];

    return (
        <div className={styles.container}>
            {!value && (
                <button
                    type="button"
                    className={styles.botaoUpload}
                    disabled={enviando}
                    onClick={handleClicarSelecionar}
                >
                    {enviando ? 'Enviando...' : temCamera ? 'Escolher foto' : 'Selecionar arquivo'}
                </button>
            )}
            <input
                ref={inputGaleriaRef}
                type="file"
                accept={accept}
                style={{ display: 'none' }}
                onChange={handleSelecionar}
            />
            {temCamera && (
                <input
                    ref={inputCameraRef}
                    type="file"
                    accept={accept}
                    capture="environment"
                    style={{ display: 'none' }}
                    onChange={handleSelecionar}
                />
            )}

            {modalAberto && (
                <div className={styles.overlay} onClick={() => setModalAberto(false)}>
                    <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                        <p className={styles.modalTitulo}>Enviar imagem</p>
                        <button
                            type="button"
                            className={styles.modalOpcao}
                            onClick={() => {
                                setModalAberto(false);
                                inputCameraRef.current?.click();
                            }}
                        >
                            <HBox style={{ alignItems: 'center', justifyContent: "center" }} gap={8}>
                                <Icon path={mdiCamera} size={1} className={styles.modalIcone} color="var(--tc-color-gray-400)" />
                                Tirar foto
                            </HBox>
                        </button>
                        <button
                            type="button"
                            className={styles.modalOpcao}
                            onClick={() => {
                                setModalAberto(false);
                                inputGaleriaRef.current?.click();
                            }}
                        >
                            <HBox style={{ alignItems: 'center', justifyContent: "center" }} gap={8}>
                                <Icon path={mdiImage} size={1} className={styles.modalIcone} color="var(--tc-color-gray-400)" />
                                Escolher da galeria
                            </HBox>
                        </button>
                        <button
                            type="button"
                            className={styles.modalCancelar}
                            onClick={() => setModalAberto(false)}
                        >
                            Cancelar
                        </button>
                    </div>
                </div>
            )}

            {value && (
                <div className={styles.arquivoSelecionado}>
                    <span className={styles.nomeArquivo} title={value.filename}>{value.filename}</span>
                    <span className={styles.tamanhoArquivo}>({formatarTamanho(value.tamanho)})</span>
                    <button type="button" className={styles.botaoRemover} onClick={() => onChange(null)}>
                        Remover
                    </button>
                </div>
            )}

            {erroEnvio && <span className={styles.erroEnvio}>{erroEnvio}</span>}
            {error && <span className={styles.erroEnvio}>{error}</span>}
        </div>
    );
};

export default CampoArquivo;
